import { useGetSettings, type WMSInfo } from '@ais-ui/websockets';
import { useEffect, useRef } from 'react';
import {
  useSetImported,
  useUserSettingsCollectionMutations,
  useUserSettingsPatch,
} from './client';
import { useImportStatus } from './importStatus';
import {
  OWNED_COLLECTION_DOMAINS,
  OWNED_DOCUMENT_DOMAINS,
} from './ownedDomains';
import { settingsPhase } from './settingsPhase';
import type { RadarOverlayItem, VectorTileItem } from './types';

/**
 * Login-time import orchestrator. Runs once per session when the global phase is
 * ≥ 2, the blob is loaded, and the server says the user has not been imported
 * yet. It seeds **every** owned domain from the blob in one pass (document
 * PATCH / collection POST-upsert) and, only on full success, flips the
 * server-authoritative `_import` flag.
 *
 * Everything is best-effort and non-blocking: if the test-only backend is slow
 * or unreachable, the flag stays unset and the import simply retries next login,
 * while the blob remains the read source throughout (see `ImportStatusProvider`).
 */
export function useUserSettingsImport(): void {
  const enabled = settingsPhase() >= 2;
  const { data: blob } = useGetSettings();
  const { imported, isLoading } = useImportStatus();
  const { mutateAsync: setImported } = useSetImported();

  // Document seeders — one PATCH mutation per owned document domain. Fixed set,
  // so hook order is stable.
  const patchCore = useUserSettingsPatch<Record<string, unknown>>('core');
  const patchMap = useUserSettingsPatch<Record<string, unknown>>('map');
  const patchVessels = useUserSettingsPatch<Record<string, unknown>>('vessels');
  const patchMessages =
    useUserSettingsPatch<Record<string, unknown>>('messages');

  // Collection seeders — one create mutation per owned collection domain.
  const wms = useUserSettingsCollectionMutations<WMSInfo>('wms', 'services');
  const tiles = useUserSettingsCollectionMutations<VectorTileItem>(
    'vector_tiles',
    'tiles',
  );
  const radar = useUserSettingsCollectionMutations<RadarOverlayItem>(
    'radar_overlays',
    'overlays',
  );

  const { mutateAsync: patchCoreAsync } = patchCore;
  const { mutateAsync: patchMapAsync } = patchMap;
  const { mutateAsync: patchVesselsAsync } = patchVessels;
  const { mutateAsync: patchMessagesAsync } = patchMessages;
  const { createItem: createWms } = wms;
  const { createItem: createTile } = tiles;
  const { createItem: createRadar } = radar;

  const ranRef = useRef(false);
  useEffect(() => {
    if (!enabled || !blob || isLoading || imported || ranRef.current) return;
    ranRef.current = true;

    // Bind each owned domain (from the registry) to its service write op.
    const documentPatchByName = {
      core: patchCoreAsync,
      map: patchMapAsync,
      vessels: patchVesselsAsync,
      messages: patchMessagesAsync,
    };
    const collectionCreateByName = {
      wms: createWms as (item: { id: string }) => Promise<unknown>,
      vector_tiles: createTile as (item: { id: string }) => Promise<unknown>,
      radar_overlays: createRadar as (item: { id: string }) => Promise<unknown>,
    };

    (async () => {
      try {
        const seeds: Promise<unknown>[] = [];
        for (const domain of OWNED_DOCUMENT_DOMAINS) {
          seeds.push(documentPatchByName[domain.name](domain.fromBlob(blob)));
        }
        for (const domain of OWNED_COLLECTION_DOMAINS) {
          const create = collectionCreateByName[domain.name];
          for (const item of domain.fromBlob(blob)) seeds.push(create(item));
        }
        // Seed everything; only mark imported once ALL owned domains succeed, so
        // a partial import retries next login (PATCH re-writes, POST upserts).
        await Promise.all(seeds);
        await setImported(true);
      } catch (error) {
        // Do NOT reset ranRef here. Re-arming on failure, combined with the
        // `blob` dependency (whose reference churns on websocket updates), turned
        // a down/slow test backend into a request storm — every blob change
        // re-fired the whole seed pass. The import is best-effort: leave the
        // guard set so this session makes exactly one attempt, and let it retry
        // on the next login/reload. The circuit breaker (settingsFetch) is the
        // second line of defence that fails fast if this ever re-fires.
        console.error(
          'User-settings login import failed; will retry next login:',
          error,
        );
      }
    })();
  }, [
    enabled,
    blob,
    imported,
    isLoading,
    setImported,
    patchCoreAsync,
    patchMapAsync,
    patchVesselsAsync,
    patchMessagesAsync,
    createWms,
    createTile,
    createRadar,
  ]);
}
